import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Store } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function ListBusiness() {
  useSeo("List Your Business — FREE | OyeBest", "List your business for free on OyeBest and start getting customers from your city.");
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasWebsite, setHasWebsite] = useState("no");
  const [f, setF] = useState({
    name: "", owner_name: "", category_slug: "", city_slug: "etah", address: "",
    phone: "", whatsapp: "", maps_url: "", description: "", services: "", website: "", instagram: "", facebook: "",
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.category_slug || !f.phone) { toast.error("Business name, category and phone are required"); return; }
    setLoading(true);
    try {
      await api.post("/business-registration", {
        ...f,
        services: f.services ? f.services.split(",").map((s) => s.trim()).filter(Boolean) : [],
        has_website: hasWebsite === "yes",
        website: hasWebsite === "yes" ? f.website : "",
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) { toast.error("Could not submit. Please try again."); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-8 h-8" /></div>
        <h1 className="font-display text-3xl font-bold">Aapka business submit ho gaya!</h1>
        <p className="text-muted-foreground mt-3">Your listing is now <b>Pending</b> admin approval. Once approved, it will be published and visible to everyone.</p>
        {hasWebsite === "no" && (
          <div className="mt-8 rounded-2xl bg-accent border border-primary/20 p-6">
            <p className="font-semibold">Need a professional website for your business?</p>
            <Button className="mt-3 rounded-full" onClick={() => navigate("/get-your-website")} data-testid="list-get-website-btn">Get Your Website</Button>
          </div>
        )}
        <Button variant="outline" className="mt-6 rounded-full" onClick={() => navigate("/")}>Back to Home</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-11 h-11 rounded-xl bg-accent text-primary flex items-center justify-center"><Store className="w-5 h-5" /></span>
        <span className="rounded-full bg-green-100 text-green-700 px-3 py-1 text-xs font-bold">100% FREE</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">List Your Business — FREE</h1>
      <p className="text-muted-foreground mt-2">Apna business list karein aur apne shehar ke customers tak pahunchein.</p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business Name *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl h-12" data-testid="lb-name" /></Field>
          <Field label="Owner Name"><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="rounded-xl h-12" data-testid="lb-owner" /></Field>
          <Field label="Category *">
            <Select value={f.category_slug} onValueChange={(v) => set("category_slug", v)}>
              <SelectTrigger className="rounded-xl h-12" data-testid="lb-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="City *">
            <Select value={f.city_slug} onValueChange={(v) => set("city_slug", v)}>
              <SelectTrigger className="rounded-xl h-12" data-testid="lb-city"><SelectValue placeholder="Select city" /></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Phone *"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-xl h-12" data-testid="lb-phone" /></Field>
          <Field label="WhatsApp"><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="rounded-xl h-12" data-testid="lb-whatsapp" /></Field>
        </div>
        <Field label="Address"><Input value={f.address} onChange={(e) => set("address", e.target.value)} className="rounded-xl h-12" data-testid="lb-address" /></Field>
        <Field label="Google Maps Location (URL)"><Input value={f.maps_url} onChange={(e) => set("maps_url", e.target.value)} className="rounded-xl h-12" placeholder="https://maps.google.com/…" data-testid="lb-maps" /></Field>
        <Field label="Description"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" rows={3} data-testid="lb-desc" /></Field>
        <Field label="Services / Menu (comma separated)"><Input value={f.services} onChange={(e) => set("services", e.target.value)} className="rounded-xl h-12" placeholder="Haircut, Facial, Spa" data-testid="lb-services" /></Field>

        <div className="rounded-2xl border border-border p-5">
          <Label className="font-semibold">Do you already have a website?</Label>
          <RadioGroup value={hasWebsite} onValueChange={setHasWebsite} className="flex gap-6 mt-3">
            <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="w-yes" data-testid="lb-website-yes" /><Label htmlFor="w-yes">Yes</Label></div>
            <div className="flex items-center gap-2"><RadioGroupItem value="no" id="w-no" data-testid="lb-website-no" /><Label htmlFor="w-no">No</Label></div>
          </RadioGroup>
          {hasWebsite === "yes" ? (
            <Input value={f.website} onChange={(e) => set("website", e.target.value)} className="rounded-xl h-12 mt-4" placeholder="Business Website URL" data-testid="lb-website-url" />
          ) : (
            <div className="mt-4 rounded-xl bg-accent p-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm font-medium">Need a professional website for your business?</p>
              <Button type="button" variant="outline" className="rounded-full border-primary/40 text-primary" onClick={() => navigate("/get-your-website")} data-testid="lb-get-website">Get Your Website</Button>
            </div>
          )}
        </div>

        <Button type="submit" disabled={loading} className="w-full rounded-full h-13 py-3 text-base font-semibold" data-testid="lb-submit">
          {loading ? "Submitting…" : "Submit Business — FREE"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">Your listing will be reviewed by our team before publishing.</p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm font-semibold">{label}</Label>{children}</div>;
}
